import { UserRoundCog } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import type { AreaId, Me } from '../api/types'
import { exitImpersonation } from '../features/auth/api'

/** Minutes left until an ISO instant, refreshed every 20 seconds. */
function useMinutesLeft(until: string): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 20_000)
    return () => window.clearInterval(timer)
  }, [])
  return Math.max(0, Math.ceil((Date.parse(until) - now) / 60_000))
}

/**
 * Amber banner for impersonated sessions (P-ADM-012). "Exit" ends the 15-minute
 * session and restores the operator's own session.
 */
export function ImpersonationBanner({ me }: { me: Me }) {
  const by = me.impersonated_by
  const [leaving, setLeaving] = useState(false)
  const minutes = useMinutesLeft(me.session_expires_at)
  if (!by) return null
  const name = me.user.display_name || me.user.email
  return (
    <section
      aria-label="Impersonation"
      className="border-b border-amber-line bg-amber-soft text-amber-ink"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 text-[13px] sm:px-6">
        <UserRoundCog aria-hidden="true" className="size-[18px] shrink-0 text-amber" />
        <p className="min-w-0 flex-1 leading-snug">
          <strong className="font-semibold">You are viewing as {name}</strong>
          <span className="hidden sm:inline"> ({me.user.email})</span>. Started by{' '}
          {by.display_name || by.email}
          {Number.isFinite(minutes) ? ` · ends in ${minutes} min` : ''}.
          <span className="hidden md:inline">
            {' '}
            MFA, identities and invitations are locked in this session.
          </span>
        </p>
        <button
          type="button"
          disabled={leaving}
          onClick={() => {
            setLeaving(true)
            void exitImpersonation()
          }}
          className="rounded-md border border-amber-line bg-surface/70 px-3 py-1 text-[13px] font-semibold text-amber-ink hover:bg-surface disabled:opacity-60"
        >
          {leaving ? 'Returning…' : 'Exit impersonation'}
        </button>
      </div>
    </section>
  )
}

/** UI-002: the whole platform is a demo with fictional data. */
export function SampleBanner({ area }: { area: AreaId }) {
  return (
    <section
      aria-label="Demo notice"
      className="border-b border-[#dce4d8] bg-[#eaf0e5] text-[#4a6157]"
    >
      <div className="flex min-h-9 items-center gap-2.5 px-4 py-1.5 text-xs sm:px-6">
        <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-leaf" />
        <strong className="font-semibold">Demo platform</strong>
        <span className="hidden sm:inline">Fictional companies, portfolios and meetings</span>
        {area === 'app' ? (
          <Link
            to="/app/methodology"
            className="ml-auto font-medium text-[#405e50] underline-offset-4 hover:underline"
          >
            About this workspace
          </Link>
        ) : null}
      </div>
    </section>
  )
}

/** UI-003 footer disclaimer with the area's data scope. */
export function ShellFooter({ scope }: { scope: string }) {
  return (
    <footer className="border-t border-line px-4 py-6 text-xs leading-relaxed text-sage sm:px-8 lg:px-10">
      <div className="mx-auto flex max-w-content flex-col gap-2 md:flex-row md:justify-between">
        <p className="max-w-3xl">
          Fictional sample records. No investment instruction, external notification or calendar
          invitation is sent. {scope}
        </p>
        <p className="shrink-0">Fruuit Engage · Fruuit Consulting</p>
      </div>
    </footer>
  )
}
